// BookmarksBar.jsx
// Named snapshots of the current dashboard filter-bus state + variable values.
// Sits directly under the dashboard title. Click a chip → apply. + button →
// prompt for a name and save the current view.
//
// Bookmark shape:
//   { id, name, filters: {...bus map}, variables: {var.id → value}, createdAt }

import React, { useState } from 'react';
import { Bookmark, Plus, X, RotateCcw } from 'lucide-react';
import { useCrossFilters } from '../../services/dashboardFilterBus.jsx';

export default function BookmarksBar({
  bookmarks = [], variableValues = {},
  hiddenPanelIds = [],
  onChange, onApplyVariables, onApplyHiddenPanels,
  darkMode = true, readOnly = false
}) {
  const { filters, restore, clearAll } = useCrossFilters();
  const [activeId, setActiveId] = useState(null);

  const T = darkMode ? {
    text: '#e5e7eb', mute: '#94a3b8', accent: '#8b5cf6',
    chip: 'transparent', chipActive: 'rgba(139,92,246,0.18)',
    border: '#334155', borderActive: '#8b5cf6', danger: '#f87171'
  } : {
    text: '#0f172a', mute: '#64748b', accent: '#7c3aed',
    chip: '#ffffff', chipActive: 'rgba(124,58,237,0.10)',
    border: '#e2e8f0', borderActive: '#7c3aed', danger: '#dc2626'
  };

  const hasCurrentFilters = Object.keys(filters || {}).length > 0
    || Object.keys(variableValues || {}).some(k => variableValues[k])
    || (hiddenPanelIds && hiddenPanelIds.length > 0);

  const saveCurrent = () => {
    const name = window.prompt('Name this view:');
    if (!name || !name.trim()) return;
    const bm = {
      id: `bm_${Date.now()}`,
      name: name.trim(),
      filters: { ...filters },
      variables: { ...variableValues },
      // Spotlight: capture which panels are currently hidden so the bookmark
      // restores the exact same subset the user prepared for the demo.
      hiddenPanelIds: [...(hiddenPanelIds || [])],
      createdAt: new Date().toISOString()
    };
    onChange?.([...(bookmarks || []), bm]);
  };

  const applyBookmark = (bm) => {
    restore(bm.filters || {});
    onApplyVariables?.(bm.variables || {});
    onApplyHiddenPanels?.(Array.isArray(bm.hiddenPanelIds) ? bm.hiddenPanelIds : []);
    setActiveId(bm.id);
  };

  const resetToDefault = () => {
    clearAll();
    onApplyVariables?.({});
    onApplyHiddenPanels?.([]);
    setActiveId(null);
  };

  const deleteBookmark = (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this bookmark?')) return;
    onChange?.((bookmarks || []).filter(b => b.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const hasBookmarks = Array.isArray(bookmarks) && bookmarks.length > 0;

  // Hide the whole strip when there's nothing to show and nothing to save.
  // (For the truly empty state, the Save button is discoverable via the toolbar.)
  if (!hasBookmarks && !hasCurrentFilters && readOnly) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      padding: '4px 0'
    }}>
      <Bookmark size={12} style={{ color: T.mute, flexShrink: 0 }} />
      <span style={{ fontSize: 10, color: T.mute, textTransform: 'uppercase',
        letterSpacing: 0.5, fontWeight: 600, marginRight: 2 }}>
        Views
      </span>

      {/* Default reset chip — visible when a filter is active */}
      {hasCurrentFilters && (
        <button
          onClick={resetToDefault}
          title="Clear all filters and variables"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 500,
            background: 'transparent', border: `1px dashed ${T.border}`,
            color: T.mute, cursor: 'pointer'
          }}
        >
          <RotateCcw size={10} /> Reset
        </button>
      )}

      {/* Saved bookmarks */}
      {(bookmarks || []).map(bm => (
        <button
          key={bm.id}
          onClick={() => applyBookmark(bm)}
          title={`${bm.name}\n${Object.keys(bm.filters || {}).length} filter(s), ${Object.keys(bm.variables || {}).length} variable(s)`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 4px 4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
            background: activeId === bm.id ? T.chipActive : T.chip,
            border: `1px solid ${activeId === bm.id ? T.borderActive : T.border}`,
            color: activeId === bm.id ? T.accent : T.text,
            cursor: 'pointer'
          }}
        >
          {bm.name}
          {!readOnly && (
            <span
              onClick={(e) => deleteBookmark(bm.id, e)}
              title="Delete bookmark"
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: 2, borderRadius: 999,
                color: T.mute, marginLeft: 2
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = T.danger}
              onMouseLeave={(e) => e.currentTarget.style.color = T.mute}
            >
              <X size={10} />
            </span>
          )}
        </button>
      ))}

      {/* Save-current button */}
      {!readOnly && (
        <button
          onClick={saveCurrent}
          disabled={!hasCurrentFilters}
          title={hasCurrentFilters
            ? 'Save the current filter + variable state as a named view'
            : 'Apply a filter or slicer first, then save the view'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
            background: hasCurrentFilters ? T.accent : 'transparent',
            border: hasCurrentFilters ? 'none' : `1px dashed ${T.border}`,
            color: hasCurrentFilters ? '#fff' : T.mute,
            cursor: hasCurrentFilters ? 'pointer' : 'not-allowed',
            opacity: hasCurrentFilters ? 1 : 0.6
          }}
        >
          <Plus size={10} /> Save view
        </button>
      )}
    </div>
  );
}
